// Rules for annual subscription plans, shared by the purchase state machine, billing and the
// purchase dialogs. Client-safe: no server imports.

type RecurringPackage = { RecurUnit: number | null; RecurInterval: string | null }
type PricedPackage = { Price: { toString(): string } }
type TaggedPricedPackage = PricedPackage & { Tag: string | null }

// Annual = a recurring package billed less often than monthly. Every annual row today is
// RecurInterval "months" / RecurUnit 12, and every place that schedules a year of credits up front
// keys off RecurUnit > 1, so this does too.
export function isAnnualPackage(pkg: RecurringPackage | null | undefined): boolean {
  if (!pkg?.RecurInterval) return false
  return (pkg.RecurUnit ?? 0) > 1 || pkg.RecurInterval.startsWith("year")
}

// "quickie-v4-annual" → "quickie-v4". Annual tags are their monthly twin's tag + "-annual" (the
// same rule ModifyPurchasePackage and billing's upgrade lookup use in the other direction).
export function monthlyTwinTag(annualTag: string | null | undefined): string | null {
  if (!annualTag?.endsWith("-annual")) return null
  return annualTag.slice(0, -"-annual".length)
}

// Upgrading a monthly plan to its annual twin ("daymaker-v4" → "daymaker-v4-annual"). There's no
// proration: the full annual price is charged now and the paid year starts now, with its first
// month of credits; the monthly period already paid for keeps its credits.
export function isAnnualUpgrade(from: { Tag: string | null }, to: { Tag: string | null }): boolean {
  return !!from.Tag && to.Tag === `${from.Tag}-annual`
}

// What switching plans charges today: the full annual price on an annual upgrade, otherwise the
// difference when moving to a dearer plan on the same billing period, or nothing. The Modified
// state charges the same (compared as Decimals, never with `>` on two Decimals — that compares
// their strings, and "100" > "60" is false).
export function planChangeDueToday(from: TaggedPricedPackage, to: TaggedPricedPackage): number {
  if (isAnnualUpgrade(from, to)) return Number(to.Price)
  return Math.max(Number(to.Price) - Number(from.Price), 0)
}

// What a Recharge (top-off) on an annual plan charges: ONE month at the monthly plan's price
// ($20 / $60 / $100 on v4), for one month of credits — the same deal a monthly subscriber's
// recharge gets. To charge a twelfth of the annual price instead ($18 / $54 / $90), return
// `annual.Price.div(12).toDecimalPlaces(2)`.
export function annualRechargePrice<T extends PricedPackage>({ monthly }: { monthly: T; annual: T }): T["Price"] {
  return monthly.Price
}

// "Save $X" on the annual upgrade: twelve months at the monthly price, minus the annual price.
export function annualSavings(monthlyPrice: number, annualPrice: number): number {
  return Math.round((monthlyPrice * 12 - annualPrice) * 100) / 100
}

// The purchase dialogs' title: "Taster Monthly Subscription", "Annual Taster Subscription".
// Annual rows are already named "Annual …", so they don't get a second "Annual".
export function subscriptionTitle(pkg: RecurringPackage & { Name: string }): string {
  if (!pkg.RecurInterval) return pkg.Name
  if (!isAnnualPackage(pkg)) return `${pkg.Name} Monthly Subscription`
  return /annual/i.test(pkg.Name) ? `${pkg.Name} Subscription` : `${pkg.Name} Annual Subscription`
}
